/**
 * Tenant-Capability Context
 * ─────────────────────────
 * Provides capabilities + adapter config of the *current effective tenant*
 * to any descendant component.
 *
 * Use `useTenantCapabilities()` to read:
 *   - capabilities (string[])
 *   - has(cap)  → boolean
 *   - adapter(slot) → { kind, config }
 *   - partnerType, tenantName
 *
 * On Super-Admin Global-Mode (no acting tenant), all caps are granted so the
 * super-admin sees every module.
 */
import { createContext, useContext, useEffect, useState, useMemo, useCallback } from "react";
import api from "@/lib/api";

const TenantCapsContext = createContext({
  capabilities: [],
  partnerType: "full_service",
  adapters: {},
  tenantName: null,
  loaded: false,
  has: () => true,
  adapter: () => ({ kind: "device_native", config: {} }),
  refresh: () => Promise.resolve(),
});

export const TenantCapsProvider = ({ children }) => {
  const [data, setData] = useState({
    capabilities: [],
    partnerType: "full_service",
    adapters: {},
    tenantName: null,
    loaded: false,
  });

  const refresh = useCallback(async () => {
    // Skip if no token — avoids hitting protected endpoint and triggering
    // global 401-redirect on public pages (invitation/onboarding).
    if (!localStorage.getItem("novatus_token")) {
      setData((d) => ({ ...d, loaded: true }));
      return;
    }
    try {
      const { data: p } = await api.get("/me/tenant-profile", { skip401Redirect: true });
      setData({
        capabilities: p.capabilities || [],
        partnerType: p.partner_type || "full_service",
        adapters: p.adapters || {},
        tenantName: p.tenant_name,
        loaded: true,
      });
    } catch (_e) {
      // Not logged in or no tenant — treat as full_service (safe default)
      setData((d) => ({ ...d, loaded: true }));
    }
  }, []);

  useEffect(() => {
    // Reload on token changes (login/logout/tenant-switch)
    const handler = () => refresh();
    window.addEventListener("novatus:auth-changed", handler);
    window.addEventListener("novatus:tenant-switched", handler);
    refresh();
    return () => {
      window.removeEventListener("novatus:auth-changed", handler);
      window.removeEventListener("novatus:tenant-switched", handler);
    };
  }, [refresh]);

  const has = useCallback((cap) => {
    if (!data.loaded) return true; // optimistic during load
    return data.capabilities.includes(cap);
  }, [data]);

  const adapter = useCallback((slot) => {
    return data.adapters[slot] || { kind: null, config: {} };
  }, [data]);

  const value = useMemo(() => ({ ...data, has, adapter, refresh }), [data, has, adapter, refresh]);
  return <TenantCapsContext.Provider value={value}>{children}</TenantCapsContext.Provider>;
};

export const useTenantCapabilities = () => useContext(TenantCapsContext);
